import { Button, Input } from 'components/atoms';
import { FormInput, LabeledStatus, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiCalendarMinus, PiCalendarPlus } from 'react-icons/pi';
import { CONTRACT_STATUS } from 'services/contract/types';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IContractFormikStatus,
  TEMPLATE_TYPE,
  type IContractTemplateForm,
} from 'services/templates/types';
import { contractTemplateUtil } from 'utils';
import { requiredEnumSelect } from 'utils/global/validation.utils';
import {
  AnyObject,
  ArraySchema,
  Schema,
  array,
  lazy,
  mixed,
  number,
  object,
  string,
} from 'yup';
import { IFormProps } from '../types';
import ContractFormTab from './tabs/ContractFormTab';
import ContractProductsTab from './tabs/ContractProductsTab';
import ContractTracksTab from './tabs/ContractTracksTab';
import ContractVideosTab from './tabs/ContractVideosTab';

export enum ContractTabs {
  form = 0,
  products = 1,
  tracks = 2,
  videos = 3,
}

export const agreementTextTemplateValidationSchema = object().shape({
  title: string().required(() => i18next.t('formik-error.required')),
  defaultValue: string().required(() => i18next.t('formik-error.required')),
});

export const agreementSelectTemplateValidationSchema = object().shape({
  title: string().required(() => i18next.t('formik-error.required')),
  defaultValue: string().required(() => i18next.t('formik-error.required')),
  options: array()
    .of(string().required(() => i18next.t('formik-error.required')))
    .min(1, () => i18next.t('formik-error.at-least-one-item')),
});

export const agreementDigitalDistributionTemplateValidationSchema =
  object().shape({
    name: string(),
    id: string().required(() => i18next.t('formik-error.required')),
    percent: number()
      .required(() => i18next.t('formik-error.required'))
      .min(0, () => i18next.t('formik-error.min-percent-value', { value: 0 }))
      .max(100, () =>
        i18next.t('formik-error.max-percent-value', { value: 100 })
      ),
  });

export const agreementTemplateConditionalItems = (
  requiredTypes: AGREEMENT_TYPE[],
  requiredSchema: Schema
) =>
  mixed().when('type', {
    is: (type: AGREEMENT_TYPE) => requiredTypes.includes(type),
    then: schema => requiredSchema,
    otherwise: schema => schema.optional().nullable(),
  });

export const agreementTemplateValidationSchema = object().shape({
  type: requiredEnumSelect(AGREEMENT_TYPE).optional(),
  textAgreementTemplate: agreementTemplateConditionalItems(
    [AGREEMENT_TYPE.text],
    agreementTextTemplateValidationSchema
  ),
  selectAgreementTemplate: agreementTemplateConditionalItems(
    [AGREEMENT_TYPE.select],
    agreementSelectTemplateValidationSchema
  ),
  distributionTemplateShares: agreementTemplateConditionalItems(
    [AGREEMENT_TYPE.shares, AGREEMENT_TYPE.defaultShare],
    agreementDigitalDistributionTemplateValidationSchema
  ),
});

export const contractItemValidationSchema = object().shape({
  id: string().required(),
  children: lazy(
    (): ArraySchema<unknown[], AnyObject, '', ''> =>
      array()
        .of(contractItemValidationSchema)
        .required(() => i18next.t('formik-error.required'))
  ),
  type: requiredEnumSelect(TEMPLATE_TYPE),
  content: mixed().when('type', {
    is: (type: TEMPLATE_TYPE) => type !== TEMPLATE_TYPE.agreement,
    then: schema => string().required(() => i18next.t('formik-error.required')),
    otherwise: schema => agreementTemplateValidationSchema,
  }),
});

const contractDetailValidationSchema = object().shape({
  id: string(),
  content: agreementTemplateValidationSchema.optional().nullable(),
  selectOptionActiveId: number().optional(),
});

const sideMenuValidationSchema = object().shape({
  detail: contractDetailValidationSchema.nullable(),
});

const contractValidationSchema = object().shape({
  sideMenu: sideMenuValidationSchema.nullable(),
  items: array().of(contractItemValidationSchema),
});

interface IContractFormProps extends IFormProps {
  indentationWidth?: number;
  indicator?: boolean;
  minDepth?: number;
  maxDepth?: number;
  initialValues?: IContractTemplateForm;
  onSubmit: (
    values: IContractTemplateForm,
    formikHelpers: FormikHelpers<IContractTemplateForm>
  ) => void;
  initialStatus?: IContractFormikStatus;
  withTabs?: boolean;
  disabledTabs?: ContractTabs[];
}

export const ContractForm = ({
  initialValues = contractTemplateUtil.form.initialValues,
  onSubmit,
  backPath,
  customBackItem,
  tittle,
  disabledTabs,
  withTabs = false,
  topbarContent,
  initialStatus = contractTemplateUtil.form.initialStatus,
}: IContractFormProps) => {
  const { t } = useTranslation();

  const formik = useFormik<IContractTemplateForm>({
    initialValues: initialValues,
    onSubmit,
    initialStatus: {
      ...initialStatus,
      displayMode:
        initialStatus.displayMode === undefined && initialValues.details
          ? initialValues.details.status !== CONTRACT_STATUS.unknown &&
            initialValues.details.status !== CONTRACT_STATUS.offer
          : initialStatus.displayMode,
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: contractValidationSchema,
  });

  const { values, setFieldValue, status } = formik;

  const getStatusOptions = [
    { value: CONTRACT_STATUS.active, label: t('enum.CONTRACT_STATUS.active') },
    initialValues.details?.status === CONTRACT_STATUS.active
      ? {
          value: CONTRACT_STATUS.withdraw,
          label: t('enum.CONTRACT_STATUS.withdraw'),
        }
      : {
          value: CONTRACT_STATUS.offer,
          label: t('enum.CONTRACT_STATUS.offer'),
        },
  ];

  return (
    <FormikProvider value={formik}>
      <Form className='w-full h-full'>
        <TopbarTabs>
          <TopBar
            title={values.details ? undefined : tittle}
            path={backPath ?? 'templates'}
            customBackItem={customBackItem}
            topbarContent={
              values.details ? (
                <div className='flex w-full items-center justify-end gap-4'>
                  <div className='flex gap-2'>
                    {values.details.withEndDate ? (
                      <Input.ComplexWrapper className='w-72' sizes={[50, 50]}>
                        <FormInput.Text
                          name='details.startDate'
                          type='date'
                          label={t('input.label.startDate')}
                          displayMode={initialStatus.displayMode}
                        />

                        <FormInput.Text
                          name='details.endDate'
                          type='date'
                          label={t('input.label.endDate')}
                          min={values.details.startDate}
                          displayMode={initialStatus.displayMode}
                        />
                      </Input.ComplexWrapper>
                    ) : (
                      <div className='w-40'>
                        <FormInput.Text
                          name='details.startDate'
                          type='date'
                          label={t('input.label.startDate')}
                          displayMode={initialStatus.displayMode}
                        />
                      </div>
                    )}
                    {!initialStatus.displayMode &&
                      (values.details.withEndDate ? (
                        <Button
                          isClear
                          onlyIcon
                          onClick={() =>
                            setFieldValue('details.withEndDate', false)
                          }>
                          <Button.Icon icon={<PiCalendarMinus />} />
                        </Button>
                      ) : (
                        <Button
                          isClear
                          onClick={() =>
                            setFieldValue('details.withEndDate', true)
                          }>
                          <Button.Text text={t('input.label.endDate')} />
                          <Button.Icon icon={<PiCalendarPlus />} />
                        </Button>
                      ))}
                  </div>
                  <div className='flex items-center gap-2 w-full justify-end'>
                    {!initialStatus.displayMode ? (
                      <div className='w-48'>
                        <FormInput.Select
                          name='details.status'
                          label={t('input.label.status')}
                          options={getStatusOptions}
                          // value={{
                          //   value: values.details.status,
                          //   label: t(
                          //     `enum.CONTRACT_STATUS.${
                          //       CONTRACT_STATUS[values.details.status]
                          //     }`
                          //   ),
                          // }}
                          displayMode={false}
                        />
                      </div>
                    ) : (
                      <LabeledStatus.Contract status={values.details.status} />
                    )}
                  </div>
                  <div>{topbarContent}</div>
                </div>
              ) : (
                topbarContent
              )
            }>
            {!!withTabs && (
              <TabsNavigation
                tabs={[
                  {
                    value: ContractTabs.form,
                    name: 'form',
                  },
                  {
                    value: ContractTabs.products,
                    name: 'products',
                  },
                  {
                    value: ContractTabs.tracks,
                    name: 'tracks',
                  },
                  {
                    value: ContractTabs.videos,
                    name: 'videos',
                  },
                ]}
                i18nextPrefix={'contract-template'}
                disabled={disabledTabs}
              />
            )}
          </TopBar>
        </TopbarTabs>
      </Form>
    </FormikProvider>
  );
};

ContractForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'form', element: <ContractFormTab /> },
    { path: 'products', element: <ContractProductsTab /> },
    { path: 'tracks', element: <ContractTracksTab /> },
    { path: 'videos', element: <ContractVideosTab /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default ContractForm;
